import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { debounce } from 'lodash';
import css from './ShopSettingsForm.module.css';
import { capitalizeFirstLetter, isArrayLength } from '../../../util/genericHelpers';
import { queryOwnListings } from '../ShopSettingsPage.duck';
import { FieldCheckbox, FieldSelect, Icons } from '../../../components';
import Drawer from 'react-modern-drawer';
import '../../TopbarContainer/Topbar/Drawer.css';
import { sortOptions } from '../../../config/configExtras';

const ShopSearchBar = ({
  intl,
  values,
  config,
  dispatch,
  isMobileLayout,
  formId,
  isShopifyAccountPage,
}) => {
  const { username = '', stock = [], sortOption = '' } = values || {};
  const [searchTerm, setSearchTerm] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);
  const filtersInitializedRef = useRef(false);

  // New state for price filter
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  
  const toggle = index => {
    setOpenIndex(prev => (prev === index ? null : index));
  };

  const performQuery = useCallback(
    term => {
      const params = {
        config,
        ...(term ? { keywords: term } : {}),
        states: 'published,closed,draft',
        page: 1,
        fetchAll: true,
      };

      if (minPrice || maxPrice) {
        params.price = `${minPrice * 100 || ''},${maxPrice * 100 || ''}`;
      }
      if (sortOption) {
        params.sort = sortOption;
      }
      if (isArrayLength(stock)) {
        params.stockMode = 'match-undefined';
      }

      dispatch(queryOwnListings(params));
    },
    [dispatch, config, stock, minPrice, maxPrice, sortOption, isShopifyAccountPage]
  );

  // FIX: Create a stable debounced ref that always calls the latest performQuery
  const performQueryRef = useRef(performQuery);
  useEffect(() => {
    performQueryRef.current = performQuery;
  }, [performQuery]);

  const debouncedNavigate = useMemo(
    () => debounce((term) => performQueryRef.current(term), 500),
    []
  );

  useEffect(() => {
    return () => debouncedNavigate.cancel();
  }, [debouncedNavigate]);

  // Re-run query whenever external filters change
  useEffect(() => {
    if (!filtersInitializedRef.current) {
      filtersInitializedRef.current = true;
      return;
    }

    performQuery(searchTerm);
  }, [stock?.length, sortOption]);

  const handleChange = e => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedNavigate(value); // Now cleanly executes with the latest scope values
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setMinPrice('');
    setMaxPrice('');
    setIsDrawerOpen(false);
    const params = {
      config,
      states: 'published,closed,draft',
      page: 1,
      fetchAll: true,
    };
    dispatch(queryOwnListings(params));

    debouncedNavigate.cancel(); 
  };

  const placeholderId = username
    ? 'ShopSettingsForm.searchPlaceholderWithUsername'
    : 'ShopSettingsForm.searchPlaceholder';
  const placeholderValues = username ? { username: capitalizeFirstLetter(username) } : {};

  const items = [
    {
      title: 'Sort',
      content: (
        <ul>
          <FieldSelect id={`${formId}.sortOption`} name="sortOption" className={css.field}>
            <option disabled value="">
              {intl.formatMessage({ id: 'ShopSettingsForm.sortOptionPlaceholder' })}
            </option>
            {sortOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FieldSelect>
        </ul>
      ),
    },
    {
      title: 'Price',
      content: (
        <ul>
          <li>
            <div className={css.minMaxPrice}>
              <input
                type="number"
                placeholder="Min"
                value={minPrice}
                onChange={e => setMinPrice(e.target.value)}
              />
              <input
                type="number"
                placeholder="Max"
                value={maxPrice}
                onChange={e => setMaxPrice(e.target.value)}
              />
              <button
                type="button"
                onClick={() => {
                  performQuery(searchTerm);
                  setMaxPrice('');
                  setMinPrice('');
                }}
              >
                <Icons name="rightAngle" />
              </button>
            </div>
          </li>
        </ul>
      ),
    },
    {
      title: 'Item Type',
      content: (
        <ul>
          <li>
            <FieldCheckbox
              id={formId ? `${formId}.stock` : 'stock'}
              name="stock"
              label={intl.formatMessage({ id: 'ShopSettingsForm.soldItems' })}
              value="stock"
            />
          </li>
        </ul>
      ),
    },
  ];

  return (
    <div className={css.searchContainer}>
      <div className={css.searchWrapper} role="search">
        <input
          type="text"
          value={searchTerm}
          onChange={handleChange}
          placeholder={intl.formatMessage({ id: placeholderId }, placeholderValues)}
          className={css.searchInput}
          aria-label={intl.formatMessage({ id: placeholderId }, placeholderValues)}
          title={intl.formatMessage({ id: placeholderId }, placeholderValues)}
        />
        {searchTerm && (
          <button
            type="button"
            className={css.searchButton}
            onClick={handleClearSearch}
            aria-label={intl.formatMessage({ id: 'ShopSettingsForm.clearSearch' })}
            title={intl.formatMessage({ id: 'ShopSettingsForm.clearSearch' })}
          >
            {intl.formatMessage({ id: 'ShopSettingsForm.clearSearch' })}
          </button>
        )}
      </div>
      <div className={css.filterSec}>
        <button
          type="button"
          className={css.filterBtn}
          onClick={() => setIsDrawerOpen(!isDrawerOpen)}
        >
          <Icons name="filter" /> {intl.formatMessage({ id: 'ShopSettingsForm.filter' })}
        </button>
        <Drawer
          enableOverlay={true}
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(!isDrawerOpen)}
          direction={isMobileLayout ? 'bottom' : 'left'}
          lockBackgroundScroll={true}
          className={css.filterDrawer}
        >
          <div className={css.drawerHead}>
            <button type="button" className={css.clearBtn} onClick={handleClearSearch}>
              {intl.formatMessage({ id: 'ShopSettingsForm.clearSearch' })}
            </button>{' '}
            {intl.formatMessage({ id: 'ShopSettingsForm.filter' })}
          </div>
          <div className={css.drawerBody}>
            {items.map((item, index) => (
              <div className={css.filterBlock} key={index}>
                <h2 onClick={() => toggle(index)}>
                  {item.title}
                  <Icons name={openIndex === index ? 'minus' : 'plus'} />
                </h2>
                {openIndex === index && <>{item.content}</>}
              </div>
            ))}
          </div>
        </Drawer>
      </div>
    </div>
  );
};

export default ShopSearchBar;
